import dayjs from 'dayjs'
import { createSignal, createMemo, onMount, For } from 'solid-js'
import { Portal } from 'solid-js/web'
import { SearchOutline } from '@/icons'
import { res2json } from '@/utils'
import { paginator } from '@lib/utils'
import account from '@/store/account'
import * as constants from '@lib/constants'
import * as games from '@games'

const statuses = {
  created: '等待报网',
  cancelled: '已退单',
  betted: '下注成功',
  failed: '下注失败',
}

const [result, setResult] = createSignal({})

export default function Betting() {
  const query = new URLSearchParams(document.location.search)
  const [date, setDate] = createSignal(query.get('date') ?? dayjs().tz('Asia/Chongqing').format('YYYY-MM-DD'))
  const [keywords, setKeywords] = createSignal(query.get('k') ?? '')
  const [type, setType] = createSignal(query.get('t') ?? '')
  const [loading, setLoading] = createSignal(true)
  const [selected, setSelected] = createSignal({})
  const [draws, setDraws] = createSignal({})

  const homeLink = () => {
    const query = new URLSearchParams(document.location.search)
    query.delete('lt')
    query.delete('gt')
    return `${document.location.pathname}?${query.toString()}`
  }
  const prevLink = createMemo(() => paginator.prevLink(result()))
  const nextLink = createMemo(() => paginator.nextLink(result()))
  const hasPaginator = createMemo(() => prevLink() || nextLink())

  onMount(() => {
    query.set('date', dayjs(date()).startOf('day').unix())
    fetch(`/api/betting?${query.toString()}`).then(res2json).then(r => {
      setResult(r.data ?? {})
      return r.data ?? {}
    }).then(r => {
      const nums = {}
      for (const row of (r.rows ?? [])) {
        const key = `${row.game}:${row.num}`
        if (!nums[key]) {
          nums[key] = { game: row.game, num: row.num }
        }
      }
      Promise.allSettled(Object.values(nums).map(e => fetch(`/api/${e.game}/${e.num}/result`).then(res2json))).then(v => {
        setDraws(Object.fromEntries(v.filter(e => e.status === 'fulfilled').map(e => [`${e.value.data.game}:${e.value.data.num}`, e.value.data])))
      })
    }).finally(() => { setLoading(false) })
  })

  function openFails(row) {
    setSelected(row)
    window.betFails.showModal()
  }

  return (
    <>
      <div class="flex flex-col gap-4">
        <div class="breadcrumbs text-sm">
          <ul>
            <li><a href="/home">后台首页</a></li>
            <li>注单列表</li>
          </ul>
        </div>
        <h2 class="text-xl">注单列表</h2>
        <form class="flex flex-wrap items-center gap-2">
          <label class="flex items-center gap-1"><span class="flex-none">日期:</span><input type="date" name="date" value={date()} onChange={e => setDate(e.target.value)} max={dayjs().tz('Asia/Chongqing').format('YYYY-MM-DD')} class="input input-bordered flex-1" /></label>
          <select name="t" value={type()} onChange={e => setType(e.target.value)} class="select select-bordered">
            <option value="">全部类型</option>
            <option value="player">玩家</option>
            <option value="robot">机/托</option>
          </select>
          <div class="join">
            <input type="text" name="k" value={keywords()} onChange={e => setKeywords(e.target.value)} class="input input-bordered join-item" placeholder="用户名或昵称" />
            <button type="submit" class="btn join-item"><span class="w-5"><SearchOutline /></span></button>
          </div>
        </form>
        <div class="overflow-x-auto">
          <table class="table whitespace-nowrap">
            <thead>
              <tr class="bg-base-200">
                <th>奖期</th>
                <th>玩家</th>
                <th>指令</th>
                <th>开奖号码</th>
                <th class="text-right">下注金额</th>
                <th class="text-right">中奖金额</th>
                <th class="text-right">返还金额</th>
                <th>状态</th>
                <th>类型</th>
                <th class="text-right">创建时间</th>
                <th>交易号</th>
              </tr>
            </thead>
            <tbody>
              <For each={result().rows ?? []}>
                {(row) => (
                  <>
                    <tr class="hover:bg-base-200">
                      <td class="text-xs font-mono">{row.num}</td>
                      <td>{row.player.username}({row.player.nickname})</td>
                      <td>{row.bet}</td>
                      <td><GameDraw draws={draws} row={row} /></td>
                      <td class="text-right">{(row.amount / 100).toFixed(2)}</td>
                      <td class={`text-right${row.prize > 0 ? ' text-error' : ''}`}>{(row.prize / 100).toFixed(2)}</td>
                      <td class={`text-right${row.refund > 0 ? ' text-error' : ''}`}>{(row.refund / 100).toFixed(2)}</td>
                      <td class="cursor-pointer"><a role="button" onClick={() => openFails(row)} class={row.status === 'failed' ? 'text-error' : ''}>{statuses[row.status] ?? row.status}</a></td>
                      <td>{row.player.is_robot ? '机/托' : '玩家'}</td>
                      <td class="text-right text-xs font-mono">{row.created_at ? dayjs(row.created_at * 1000).format('YYYY-MM-DD HH:mm:ss') : '-'}</td>
                      <td class="text-xs font-mono">{row.transaction_id}</td>
                    </tr>
                    {!import.meta.env.PROD && (
                      <tr class="hover:bg-base-200">
                        <td colspan="13" class="py-1"><input type="text" value={row.bets ?? '-'} class="w-full text-gray-500 font-mono text-[9px] bg-transparent" disabled /></td>
                      </tr>
                    )}
                  </>
                )}
              </For>
            </tbody>
          </table>
        </div>
        {hasPaginator() && (
          <div class="flex items-center justify-end join">
            <button type="button" onClick={() => document.location.href = homeLink()} class="btn join-item font-normal" disabled={!prevLink()}>首页</button>
            <button type="button" onClick={() => document.location.href = prevLink()} class="btn join-item font-normal" disabled={!prevLink()}>上一页</button>
            <button type="button" onClick={() => document.location.href = nextLink()} class="btn join-item font-normal" disabled={!nextLink()}>下一页</button>
          </div>
        )}
      </div>

      <Portal mount={document.body}>
        {loading() && (<div class="fixed left-2 right-2 bottom-0"><progress class="progress progress-success w-full"></progress></div>)}

        <FailsDialog slip={selected} />
      </Portal>
    </>
  )
}

function FanNumber({ game, draw }) {
  const n = games.basis[account().game].calcFanNumber(game, draw)
  if (!Number.isInteger(n)) {
    return <></>
  }
  return (
    <>
      <span class="text-gray-500">-</span>
      <span class="text-success">{n % 4 || 4}</span>
    </>
  )
}

function FailsDialog({ slip }) {
  const [loading, setLoading] = createSignal(false)
  const [rows, setRows] = createSignal([])

  onMount(() => {
    new MutationObserver((e) => {
      if (e[0].target.open) {
        setLoading(true)
        setRows([])
        fetch(`/api/betting/${slip().transaction_id}/fails`).then(res2json).then(r => { setRows(r.data ?? {}) }).finally(() => setLoading(false))
      }
    }).observe(window.betFails, { attributes: true })
  })

  function cancel() {
    window.betFails.close()
  }

  return (
    <dialog id="betFails" class="modal">
      <div class="modal-box flex flex-col gap-4">
        <button type="button" onClick={() => cancel()} class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">✕</button>
        <h3 class="text-lg">错误信息</h3>
        {loading() ? <span class="loading loading-dots loading-md"></span> : (
          <div class="form-control">
            <For each={rows()}>
              {(row) => (
                <div>{row.bet}: {row.reason.replaceAll(/<.+?>/g, '')}</div>
              )}
            </For>
          </div>
        )}
        <div class="modal-action flex gap-4">
          <button type="button" onClick={() => cancel()} class="btn px-8">确认</button>
        </div>
      </div>
    </dialog>
  )
}

function GameDraw({ draws, row }) {
  const key = `${row.game}:${row.num}`
  return (
    <>
      {draws()[key] && (<span>{Array.isArray(draws()[key].draw) ? draws()[key].draw.join(',') : draws()[key].draw}</span>)}
      {draws()[key] && row.method === 'FAN' && (<FanNumber game={account().game} draw={draws()[key].draw} />)}
    </>
  )
}
